/**
 * ForceReviewModal — send a stuck job with a captured load to In Review (DEC-321 / OPEN-082).
 *
 * DEC-355 (OPEN-151): also opened from the job detail, for an in-progress job with a captured
 * load that is NOT on the stuck list yet (the driver was active recently). Corbin ruled that is
 * allowed at any time, but the dialog must warn: once the job is in review, anything the
 * driver's phone sends is set aside by the late-data guard (DEC-321 PR 1), not added to the
 * ticket. `row.onStuckList === false` shows that warning with the driver's last activity.
 *
 * POST /v1/jobs/:id/force-review with { reason }. Such a job cannot be cancelled (DEC-315 B
 * would lose the load), so this is how it gets finished: the API moves it to IN_REVIEW, stops
 * the driver's clock at their last real activity, and adds the ack-required FORCED_BY_DISPATCH
 * flag. The load does not count as delivered. The API refuses a job that is no longer stuck, a
 * job with no captured load, and roles outside the stuck-cancel set; the modal shows the API's
 * own sentence for each refusal verbatim, which is why it uses useMutation + authedFetch
 * directly (useApiMutation keeps only the status and code, never fields[0].reason).
 *
 * Structure and styling mirror StuckJobCancelModal. On success: invalidate ['jobs'] (the stuck
 * list and job board) and ['inreview'] (queue, count, detail), toast, call onSent.
 */

import * as React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { authedFetch, useApiOpts } from '@lodex-pro/web-shared';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import { formatAge } from './stuck-jobs-format';

/**
 * The job being sent. A StuckJobRow satisfies it as-is (and is on the list by definition); the
 * job detail passes `onStuckList` from the API's `sendToReview.onStuckList`.
 */
export interface ForceReviewTarget {
  readonly jobId: string;
  readonly haulId: string | null;
  readonly driverName: string | null;
  readonly lastActivityAt: string;
  /** DEC-355 — false: the driver was active recently, so the dialog warns. Absent = on list. */
  readonly onStuckList?: boolean;
}

/** The API requires a trimmed reason of 8+: it becomes the clock stop's reason (DEC-277). */
const REASON_MIN = 8;

const GENERIC_FAILURE = 'Could not send the job to review. Try again.';

interface ErrorEnvelope {
  readonly error?: {
    readonly code?: unknown;
    readonly message?: unknown;
    readonly fields?: ReadonlyArray<{ readonly path?: unknown; readonly reason?: unknown }>;
  };
}

/**
 * The refusal sentence to show: fields[0].reason when it is a string, else error.message,
 * else a generic line. Same precedence as StuckJobCancelModal's readCancelError.
 */
async function readForceError(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as ErrorEnvelope;
    const reason = body.error?.fields?.[0]?.reason;
    if (typeof reason === 'string') return reason;
    const message = body.error?.message;
    return typeof message === 'string' ? message : GENERIC_FAILURE;
  } catch {
    return GENERIC_FAILURE;
  }
}

export interface ForceReviewModalProps {
  readonly row: ForceReviewTarget | null;
  readonly open: boolean;
  readonly onClose: () => void;
  /** Called after a successful send so the parent can dismiss the modal. */
  readonly onSent: () => void;
  /**
   * DEC-329 — the click files a request a manager must approve (SUPERVISOR_REQUIRED, a plain
   * dispatcher). Only the wording changes: the API decides, and answers 202 for a request.
   */
  readonly asRequest?: boolean;
}

const textareaStyle: React.CSSProperties = {
  width: '100%',
  minHeight: '80px',
  padding: '8px 10px',
  fontSize: '14px',
  backgroundColor: 'var(--color-bg-tertiary)',
  border: '1px solid var(--color-border-secondary)',
  borderRadius: '10px',
  color: 'var(--color-text-primary)',
  resize: 'vertical',
};

const labelStyle: React.CSSProperties = {
  fontSize: '11px',
  fontWeight: 600,
  color: 'var(--color-text-tertiary)',
  display: 'block',
  marginBottom: '4px',
};

export function ForceReviewModal({
  row,
  open,
  onClose,
  onSent,
  asRequest = false,
}: ForceReviewModalProps): React.JSX.Element {
  const apiOpts = useApiOpts();
  const queryClient = useQueryClient();
  const [reason, setReason] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setReason('');
      setError(null);
    }
  }, [open]);

  const mutation = useMutation<number, Error, { jobId: string; payload: string }>({
    mutationFn: async ({ jobId, payload }) => {
      const res = await authedFetch(apiOpts, `/v1/jobs/${jobId}/force-review`, {
        method: 'POST',
        body: payload,
      });
      if (!res.ok) throw new Error(await readForceError(res));
      return res.status;
    },
    onSuccess: (status) => {
      void queryClient.invalidateQueries({ queryKey: ['jobs'] });
      void queryClient.invalidateQueries({ queryKey: ['inreview'] });
      // 202 = DEC-329 request filed; 200 = forced now.
      toast.success(status === 202 ? 'Sent to a manager for approval' : 'Sent to review');
      onSent();
    },
    onError: (err) => {
      setError(err.message);
    },
  });

  const recentlyActive = row !== null && row.onStuckList === false;
  const reasonValid = reason.trim().length >= REASON_MIN;
  const canSubmit = reasonValid && !mutation.isPending;

  const handleConfirm = React.useCallback((): void => {
    if (!reasonValid || row === null) return;
    setError(null);
    mutation.mutate({ jobId: row.jobId, payload: JSON.stringify({ reason: reason.trim() }) });
  }, [reasonValid, row, reason, mutation]);

  return (
    <Dialog
      open={open}
      onOpenChange={(next): void => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="w-[calc(100vw-2rem)] max-w-md">
        <DialogHeader>
          <DialogTitle>
            {asRequest
              ? `Ask a manager to send to review — ${row?.haulId ?? 'job'}`
              : `Send to review — ${row?.haulId ?? 'job'}`}
          </DialogTitle>
        </DialogHeader>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)', margin: 0 }}>
            This sends the job to In Review so a reviewer can bill what was captured. The
            driver&apos;s clock stops at their last activity. The load will not count as delivered.
            {asRequest
              ? ' Your company requires a manager to approve this first; nothing changes until they do.'
              : null}
          </p>

          {recentlyActive && row !== null ? (
            <p
              role="alert"
              data-testid="force-review-recent-activity"
              style={{
                fontSize: '14px',
                margin: 0,
                padding: '8px 10px',
                borderRadius: '12px',
                border: '1px solid var(--color-warning)',
                backgroundColor: 'rgba(246, 182, 94, 0.12)',
                color: 'var(--color-text-primary)',
              }}
            >
              {row.driverName ?? 'The driver'} last did something{' '}
              {formatAge(row.lastActivityAt, Date.now())}. Anything{' '}
              {row.driverName === null ? "the driver's" : `${row.driverName}'s`} phone sends after
              this is set aside, not added to the ticket.
            </p>
          ) : null}

          <label htmlFor="force-review-reason" style={labelStyle}>
            Reason
          </label>
          <textarea
            id="force-review-reason"
            value={reason}
            onChange={(e): void => setReason(e.target.value)}
            placeholder="Why is this job being sent to review? (min 8 characters)"
            style={textareaStyle}
            aria-label="Send to review reason"
          />
          {reason.trim().length > 0 && !reasonValid && (
            <span style={{ fontSize: '12px', color: 'var(--color-text-tertiary)' }}>
              Reason must be at least {REASON_MIN} characters.
            </span>
          )}
        </div>

        {error !== null && (
          <p role="alert" style={{ fontSize: '13px', color: 'var(--color-danger)', margin: 0 }}>
            {error}
          </p>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {recentlyActive ? 'Keep in progress' : 'Keep on stuck list'}
          </Button>
          <Button
            type="button"
            disabled={!canSubmit}
            onClick={handleConfirm}
            style={{
              backgroundColor: 'var(--color-warning)',
              color: 'var(--color-on-status)',
              borderColor: 'var(--color-warning)',
              opacity: canSubmit ? 1 : 0.6,
            }}
          >
            {mutation.isPending ? 'Sending…' : asRequest ? 'Ask a manager' : 'Send to review'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
